import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Minus, Boxes, History, Barcode, AlertTriangle, Loader2, Search } from "lucide-react";
import { api, inr, fileUrl, openPdf, errorMessage } from "@/lib/api";
import { Pagination, usePaged } from "@/components/Pagination";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };

export default function Inventory() {
  const [tab, setTab] = React.useState("overview");
  const { data, refetch } = useQuery({ queryKey: ["inv-summary"], queryFn: () => api.get("/inventory/summary").then(r => r.data) });
  const t = data?.totals || {};
  const tabs = [["overview", "Stock by product", Boxes], ["history", "Stock history", History], ["pieces", "Barcode pieces", Barcode]];
  return (
    <div data-testid="admin-inventory">
      <h1 className="h2-editorial mb-1">Inventory</h1>
      <p className="text-sm text-neutral-500 mb-6">Stock goes down automatically when an order is placed (Cash on Delivery) or paid (online), and goes back up when an order is cancelled or a return is received.</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[["In stock", t.in_stock], ["Sold", t.sold], ["Low stock", t.low, "text-[#8a6d00]"], ["Sold out", t.out, "text-[#991B1B]"], ["Stock value", t.stock_value != null ? inr(t.stock_value) : "–"]].map(([l, v, c]) => (
          <div key={l} className="bg-white border p-4"><div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{l}</div><div className={`text-2xl font-semibold ${c || "text-[#0E4D34]"}`}>{v ?? "–"}</div></div>
        ))}
      </div>
      <div className="flex gap-1 border-b mb-5 overflow-x-auto">
        {tabs.map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`inv-tab-${k}`}
                  className={`px-4 py-2.5 text-sm flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-[#0E4D34] text-[#0E4D34] font-semibold" : "border-transparent text-neutral-600"}`}><I size={14}/>{l}</button>
        ))}
      </div>
      {tab === "overview" && <Overview data={data} refetch={refetch}/>}
      {tab === "history" && <StockHistory/>}
      {tab === "pieces" && <Pieces products={data?.products || []}/>}
    </div>
  );
}

function Overview({ data, refetch }) {
  const qc = useQueryClient();
  const [q, setQ] = React.useState("");
  const [f, setF] = React.useState("");
  const [adjust, setAdjust] = React.useState(null);
  const list = (data?.products || []).filter(p => (!f || p.status === f) && (!q || [p.name, p.category].some(v => (v || "").toLowerCase().includes(q.toLowerCase()))));
  const pg = usePaged(list, 20, { resetKey: `${q}|${f}` });
  const done = () => { refetch(); qc.invalidateQueries({ queryKey: ["inv-moves"] }); qc.invalidateQueries({ queryKey: ["adm-products"] }); qc.invalidateQueries({ queryKey: ["shop-all"] }); };
  return (
    <div>
      <div className="flex gap-2 flex-wrap mb-3 items-center">
        <div className="relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search product or category" className="border border-neutral-300 pl-8 p-2.5 text-sm w-64"/></div>
        {[["", "All"], ["low", `Low (≤ ${data?.totals?.threshold ?? 3})`], ["out", "Sold out"], ["ok", "In stock"]].map(([k, l]) => (
          <button key={k} onClick={() => setF(k)} className={`text-xs px-3 py-1.5 border rounded-full ${f === k ? "bg-[#0E4D34] text-white border-[#0E4D34]" : "bg-white"}`}>{l}</button>
        ))}
      </div>
      <div className="bg-white border overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-neutral-50 text-xs uppercase tracking-widest"><tr>{["Product", "Category", "Price", "In stock", "Sold", "Removed", ""].map(h => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
          <tbody>
            {!data && <tr><td colSpan={7} className="p-6 text-center text-neutral-500">Loading…</td></tr>}
            {pg.items.map(p => (
              <tr key={p.id} className="border-t" data-testid={`inv-row-${p.slug}`}>
                <td className="p-3"><div className="flex items-center gap-3">{p.image ? <img src={fileUrl(p.image)} alt="" className="w-9 h-11 object-cover rounded"/> : <span className="w-9 h-11 bg-neutral-100 rounded"/>}
                  <div><Link to={`/admin/products/${p.id}`} className="font-medium hover:text-[#0E4D34]">{p.name}</Link>{!p.active && <div className="text-[10px] uppercase text-neutral-400">hidden</div>}</div></div></td>
                <td className="p-3 text-neutral-600">{p.category || "—"}</td>
                <td className="p-3">{inr(p.price)}</td>
                <td className="p-3"><span className={`font-semibold ${p.status === "out" ? "text-[#991B1B]" : p.status === "low" ? "text-[#8a6d00]" : "text-[#0E4D34]"}`} data-testid="inv-stock">{p.in_stock}</span>
                  {p.status === "out" && <span className="ml-2 text-[10px] uppercase bg-[#FDECEC] text-[#991B1B] px-1.5 py-0.5">sold out</span>}
                  {p.status === "low" && <span className="ml-2 text-[10px] uppercase bg-[#FFF7E0] text-[#8a6d00] px-1.5 py-0.5 inline-flex items-center gap-1"><AlertTriangle size={10}/>low</span>}</td>
                <td className="p-3">{p.sold}</td>
                <td className="p-3 text-neutral-500">{p.removed || 0}</td>
                <td className="p-3"><div className="flex gap-1 justify-end">
                  <button onClick={() => setAdjust({ p, dir: 1 })} className="text-xs border px-2.5 py-1.5 flex items-center gap-1 hover:border-[#0E4D34]" data-testid={`inv-add-${p.slug}`}><Plus size={12}/>Add</button>
                  <button onClick={() => setAdjust({ p, dir: -1 })} disabled={!p.in_stock} className="text-xs border px-2.5 py-1.5 flex items-center gap-1 hover:border-[#991B1B] disabled:opacity-30"><Minus size={12}/>Remove</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...pg.props} label="products"/>
      {adjust && <AdjustDialog {...adjust} onClose={() => setAdjust(null)} onDone={() => { setAdjust(null); done(); }}/>}
    </div>
  );
}

function AdjustDialog({ p, dir, onClose, onDone }) {
  const [n, setN] = React.useState(1);
  const [reason, setReason] = React.useState(dir > 0 ? "New stock arrived" : "Damaged");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/inventory/adjust", { product_id: p.id, change: dir * Math.abs(Number(n) || 0), reason, note });
      toast.success(`${p.name}: now ${r.data.stock} in stock`); onDone();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <form onSubmit={save} className="relative bg-white w-full max-w-sm p-6 space-y-3" data-testid="adjust-dialog">
        <h3 className="font-semibold">{dir > 0 ? "Add stock" : "Remove stock"} — {p.name}</h3>
        <p className="text-xs text-neutral-500">Now {p.in_stock} in stock. {dir > 0 ? "New barcode pieces are created (print labels from the Barcode pieces tab)." : "Pieces are marked removed (not sold) and kept in the history."}</p>
        <div><label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1">How many</label><input type="number" min="1" max={dir < 0 ? p.in_stock : 500} value={n} onChange={e => setN(e.target.value)} className="w-full border p-2.5 text-sm" data-testid="adjust-qty"/></div>
        <div><label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1">Reason</label>
          <select value={reason} onChange={e => setReason(e.target.value)} className="w-full border p-2.5 text-sm bg-white">
            {(dir > 0 ? ["New stock arrived", "Found in store (count correction)", "Returned at store counter"] : ["Damaged", "Lost / missing", "Given as gift / sample", "Count correction"]).map(x => <option key={x}>{x}</option>)}
          </select></div>
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)" className="w-full border p-2.5 text-sm"/>
        <div className="flex gap-2 pt-1">
          <button disabled={busy} className="btn-silk flex-1 disabled:opacity-50" data-testid="adjust-save">{busy ? <Loader2 size={14} className="animate-spin"/> : "Save"}</button>
          <button type="button" onClick={onClose} className="btn-outline-silk">Cancel</button>
        </div>
      </form>
    </div>
  );
}

const REASON_COLOR = (r) => /sale/.test(r) ? "text-[#991B1B]" : /return|cancel/.test(r) ? "text-[#1D4E89]" : "text-neutral-700";

function StockHistory() {
  const { data } = useQuery({ queryKey: ["inv-moves"], queryFn: () => api.get("/inventory/movements?limit=3000").then(r => r.data) });
  const [q, setQ] = React.useState("");
  const list = (data || []).filter(m => !q || [m.product_name, m.reason, m.ref].some(v => (v || "").toLowerCase().includes(q.toLowerCase())));
  const pg = usePaged(list, 30, { resetKey: q });
  return (
    <div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search product, reason or order no." className="border border-neutral-300 p-2.5 text-sm w-72 mb-3"/>
      <div className="bg-white border overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="stock-history">
          <thead className="bg-neutral-50 text-xs uppercase tracking-widest"><tr>{["When", "Product", "Change", "Stock after", "Reason", "Reference", "By"].map(h => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-neutral-500">No stock changes yet.</td></tr>}
            {pg.items.map(m => (
              <tr key={m.id} className="border-t">
                <td className="p-3 text-xs text-neutral-500 whitespace-nowrap">{fmt(m.created_at)}</td>
                <td className="p-3">{m.product_name}</td>
                <td className={`p-3 font-semibold ${m.change > 0 ? "text-[#0E4D34]" : "text-[#991B1B]"}`}>{m.change > 0 ? `+${m.change}` : m.change}</td>
                <td className="p-3">{m.stock_after}</td>
                <td className={`p-3 capitalize ${REASON_COLOR(m.reason)}`}>{m.reason}{m.note ? <span className="text-xs text-neutral-500"> · {m.note}</span> : null}</td>
                <td className="p-3 font-mono text-xs">{m.ref || "—"}</td>
                <td className="p-3 text-xs text-neutral-500">{m.by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...pg.props} label="changes"/>
    </div>
  );
}

function Pieces({ products }) {
  const [selected, setSelected] = React.useState("");
  const [status, setStatus] = React.useState("");
  const { data: pieces } = useQuery({ queryKey: ["pieces", selected], queryFn: () => api.get(`/pieces${selected ? `?product_id=${selected}` : ""}`).then(r => r.data) });
  const names = Object.fromEntries(products.map(p => [p.id, p.name]));
  const list = (pieces || []).filter(p => !status || p.status === status);
  const pg = usePaged(list, 25, { resetKey: `${selected}|${status}` });
  return (
    <div>
      <div className="bg-white border p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div><label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">Product</label>
          <select value={selected} onChange={e => setSelected(e.target.value)} className="border border-neutral-300 p-2.5 text-sm bg-white max-w-xs" data-testid="inv-product"><option value="">All</option>{products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
        <div><label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">Show</label>
          <select value={status} onChange={e => setStatus(e.target.value)} className="border border-neutral-300 p-2.5 text-sm bg-white"><option value="">All pieces</option><option value="in_stock">In stock</option><option value="sold">Sold</option><option value="removed">Removed</option></select></div>
        <p className="text-xs text-neutral-500 basis-full">Each piece has its own barcode. Use "Add" on the Stock tab to create new pieces, then print their labels here.</p>
      </div>
      <div className="bg-white border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase tracking-widest"><tr>{["Barcode", "Product", "Status", "Location", ""].map(h => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
          <tbody>
            {pg.items.map(p => (
              <tr key={p.id} className="border-t"><td className="p-3 font-mono">{p.barcode}</td><td className="p-3">{names[p.product_id] || "-"}</td>
                <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${p.status === "in_stock" ? "bg-[#E8F3EE] text-[#0E4D34]" : p.status === "sold" ? "bg-neutral-100" : "bg-[#FDECEC] text-[#991B1B]"}`}>{p.status.replace("_", " ")}</span></td>
                <td className="p-3">{p.location}</td>
                <td className="p-3"><button type="button" onClick={() => openPdf(`/pieces/${p.barcode}/label`).catch(e => toast.error(errorMessage(e)))} className="text-[#0E4D34] underline text-xs">PDF label</button></td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...pg.props} label="pieces"/>
    </div>
  );
}
